import { Box, Button, Chip, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { formatRelativeTime } from '../../Services/FormatRelativeTime';
import { COLORS } from '../../Styles/Colors';
import { RestockPriority, VendingRestockQueueItem } from '../../Types/vending';
import { VendingSectionCard } from './VendingStyles';

const PRIORITY_CONFIG: Record<RestockPriority, { label: string; color: string; bg: string }> = {
  critical: { label: 'Critical', color: COLORS.red, bg: `${COLORS.red}14` },
  high: { label: 'High', color: COLORS.orange, bg: `${COLORS.orange}18` },
  medium: { label: 'Medium', color: '#1565c0', bg: '#1565c014' },
};

interface RestockPriorityChipProps {
  priority: RestockPriority;
}

export const RestockPriorityChip = ({ priority }: RestockPriorityChipProps) => {
  const config = PRIORITY_CONFIG[priority];
  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.bg,
        color: config.color,
        fontWeight: 700,
        letterSpacing: '0.03em',
      }}
    />
  );
};

interface RestockMachineCardProps {
  item: VendingRestockQueueItem;
  highlighted?: boolean;
  completing?: boolean;
  onComplete: (machineId: number) => void;
}

const machineDisplayName = (item: VendingRestockQueueItem) =>
  item.machine_name || `VM-${item.provider_machine_id}`;

const customerDisplay = (item: VendingRestockQueueItem) =>
  item.customer_name || (item.location_map_status === 'mapped' ? item.customer_label : null) || 'Unassigned';

const locationDisplay = (item: VendingRestockQueueItem) => {
  if (item.location_name) return item.location_name;
  const parts = [item.city, item.state].filter(Boolean);
  if (parts.length) return parts.join(', ');
  return 'Unassigned';
};

export const RestockMachineCard = ({
  item,
  highlighted = false,
  completing = false,
  onComplete,
}: RestockMachineCardProps) => (
  <VendingSectionCard
    id={`restock-machine-${item.machine_id}`}
    sx={{
      mb: 2,
      borderColor: highlighted ? COLORS.teal : undefined,
      boxShadow: highlighted ? `0 0 0 2px ${COLORS.teal}30` : undefined,
    }}
  >
    <Box sx={{ p: { xs: 2, md: 2.5 } }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{ color: COLORS.teal, fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1.4 }}
          >
            {customerDisplay(item).toUpperCase()}
          </Typography>
          <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, lineHeight: 1.3 }}>
            {locationDisplay(item)}
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={{ color: COLORS.lightGray, mt: 0.25 }}>
            Machine {machineDisplayName(item)}
          </Typography>
        </Box>
        <RestockPriorityChip priority={item.priority} />
      </Box>

      <Typography variant="body2" sx={{ color: COLORS.darkGray, fontWeight: 600, mb: 0.5 }}>
        {item.product_count} product{item.product_count === 1 ? '' : 's'} to replenish
      </Typography>
      <Typography variant="body2" sx={{ color: COLORS.teal, fontWeight: 700, mb: 1.5 }}>
        {item.total_units} total unit{item.total_units === 1 ? '' : 's'}
      </Typography>
      {item.last_refill_at && (
        <Typography variant="caption" sx={{ color: COLORS.lightGray, display: 'block', mb: 1.5 }}>
          Last restock: {formatRelativeTime(item.last_refill_at)}
        </Typography>
      )}

      <Box
        sx={{
          borderTop: `1px solid ${COLORS.lightGray}35`,
          borderBottom: `1px solid ${COLORS.lightGray}35`,
          py: 1,
          mb: 2,
        }}
      >
        {item.lines.map((line) => (
          <Box
            key={line.slot_id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              py: 0.75,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.darkGray }}>
                {line.product?.name || 'Unknown product'}
              </Typography>
              {line.product?.provider_sku && (
                <Typography variant="caption" sx={{ color: COLORS.lightGray }}>
                  SKU {line.product.provider_sku}
                  {line.location_key ? ` · Slot ${line.location_key}` : ''}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.teal, flexShrink: 0 }}>
              Add {line.qty_to_bring}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          component={Link}
          to={`/vending/machines/${item.machine_id}/inventory`}
          variant="outlined"
          sx={{
            borderColor: COLORS.teal,
            color: COLORS.teal,
            fontWeight: 600,
          }}
        >
          View Machine
        </Button>
        <Button
          variant="contained"
          disabled={completing}
          onClick={() => onComplete(item.machine_id)}
          sx={{
            backgroundColor: COLORS.teal,
            fontWeight: 600,
            '&:hover': { backgroundColor: COLORS.teal },
          }}
        >
          {completing ? 'Saving…' : 'Mark Restock Complete'}
        </Button>
      </Box>
    </Box>
  </VendingSectionCard>
);

export default RestockMachineCard;
